import type { Person } from '@/types'
import { cn, initials } from '@/lib/utils'

const sizes = {
  sm: 'h-6 w-6 text-[10px]',
  md: 'h-8 w-8 text-xs',
  lg: 'h-10 w-10 text-sm',
  /* The account screen, where the photo is the subject rather than a label
     beside a name. Nothing else should reach for this. */
  xl: 'h-24 w-24 text-2xl',
} as const

export function Avatar({
  person,
  size = 'md',
  className,
}: {
  person: Pick<Person, 'firstName' | 'lastName' | 'avatarColor'> & { avatarUrl?: string | null } | undefined
  size?: keyof typeof sizes
  className?: string
}) {
  if (person?.avatarUrl) {
    return (
      <img
        src={person.avatarUrl}
        alt={`${person.firstName} ${person.lastName}`}
        loading="lazy"
        className={cn('inline-block shrink-0 rounded-full object-cover', sizes[size], className)}
        style={{ backgroundColor: person.avatarColor }}
      />
    )
  }

  if (!person) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground',
          sizes[size],
          className,
        )}
        aria-hidden
      >
        ?
      </span>
    )
  }
  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white', sizes[size], className)}
      style={{ backgroundColor: person.avatarColor }}
      title={`${person.firstName} ${person.lastName}`}
    >
      {initials(person.firstName, person.lastName)}
    </span>
  )
}

export function AvatarStack({
  people,
  max = 4,
  size = 'sm',
}: {
  people: Array<
    Pick<Person, 'id' | 'firstName' | 'lastName' | 'avatarColor'> & { avatarUrl?: string | null }
  >
  max?: number
  size?: keyof typeof sizes
}) {
  // Someone holding two positions on the same plan arrives here twice; showing
  // one face per person is what a reader expects, and duplicate React keys
  // would let one of them be dropped at random anyway.
  const unique = people.filter(
    (person, index) => people.findIndex((other) => other.id === person.id) === index,
  )
  const shown = unique.slice(0, max)
  const overflow = unique.length - shown.length
  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((person) => (
        <Avatar key={person.id} person={person} size={size} className="ring-2 ring-card" />
      ))}
      {overflow > 0 ? (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground ring-2 ring-card',
            sizes[size],
          )}
        >
          +{overflow}
        </span>
      ) : null}
    </div>
  )
}
